import {assert, assertAndContinue} from 'Asserts';
import {BatchSchema} from 'constants/SampleConstants';
import {EntitySelectionMode} from 'inventory/InventoryConstants';
import {ClientId} from 'notebook/ELNConstants';
import InventoryStructuredTableMode from 'notebook/integrations/samples/InventoryStructuredTableMode';
import {TransferStructuredTableDestinationType} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import {
  ArchivedFieldColumnDefinition,
  ColumnDefinition,
  EXISTING_BIOENTITY_COLUMN_NAME,
  MeasurementAmountColumnDefinition,
  MissingFieldColumnDefinition,
  ProcessTaskStatusColumnDefinition,
  SchemaFieldColumnDefinition,
  UnitReferenceColumnDefinition,
  UnitSelectorColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import isReadOnly from 'notebook/structured-tables/columns/isReadOnly';
import {toSchemaFieldColumnDefinition} from 'notebook/structured-tables/columns/toSchemaFieldColumnDefinition';
import {
  BasesStructuredTableColumn,
  BaseTagValueStructuredTableColumn,
  BioentitySelectorStructuredTableColumn,
  BoxStructuredTableColumn,
  BulkAssemblyBinSelectorStructuredTableColumn,
  BulkAssemblyFragmentEndpointSelectorStructuredTableColumn,
  BulkAssemblyFragmentSelectorStructuredTableColumn,
  BulkAssemblyFragmentSequenceStructuredTableColumn,
  BaseExistingBioentityStructuredTableColumn,
  ComplexPolymerComponentStructuredTableColumn,
  ConcentrationStructuredTableColumn,
  ContainersStructuredTableColumn,
  CustomStructuredTableColumn,
  DedicatedLookupStructuredTableColumn,
  FillPlateSourceChipStructuredTableColumn,
  FillPlateSourceContainerChipStructuredTableColumn,
  MeasurementAmountStructuredTableColumn,
  OptionSelectorStructuredTableColumn,
  ProcedureParameterConfirmedInStructuredTableColumn,
  PlateStructuredTableColumn,
  ProcedureStructuredTableColumn,
  ProcedureSchemaFieldTypeStructuredTableColumn,
  ProcessTaskStatusStructuredTableColumn,
  QuantityStructuredTableColumn,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  StructuredTableKind,
  TagValueStructuredTableColumn,
  TestDefinitionStructuredTableColumn,
  UnitReferenceColumnDisplayMode,
  UnitReferenceStructuredTableColumn,
  UnitSelectorStructuredTableColumn,
  PlateMapMaterialInputStructuredTableColumn,
  OptionSearchSelectorStructuredTableColumn,
  AntibodyDomainStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {
  ContainersColumnMode,
  TransferTableColumnGroup,
} from 'notebook/transfer-tables/TransferTableConstants';
import findProcessRelatedSchemaId from 'processes/tables/findProcessRelatedSchemaId';
import {
  ParentStorableType,
  PlateSchema,
  PlateSchemaType,
  StorableSchema,
} from 'samples/storables/StorableSchemaConstants';
import {BioentityLinkSchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaInterfaceSummary} from 'schemas/schema-interface/SchemaInterfaceConstants';
import {Schema} from 'schemas/SchemaConstants';
import * as SchemaPredicates from 'schemas/SchemaPredicates';
import APIUtil from 'util/APIUtil';
import {StageRun} from 'workflows/WorkflowConstants';

import findMatchingSchemaFieldForColumn from './findMatchingSchemaFieldForColumn';
import isMissingFieldColumn from './isMissingFieldColumn';
import {
  isBoxStructuredTableColumn,
  isConcentrationStructuredTableColumn,
  isContainersStructuredTableColumn,
  isExistingBioentityStructuredTableColumn,
  isMeasurementAmountStructuredTableColumn,
  isParentStorableStructuredTableColumn,
  isPlateStructuredTableColumn,
  isProcessTaskStatusStructuredTableColumn,
  isQuantityStructuredTableColumn,
  isTagValueStructuredTableColumn,
  isUnitReferenceStructuredTableColumn,
  isUnitSelectorStructuredTableColumn,
} from './TypePredicates';
import {getPredicateType} from './UnitTypePredicates';

function toQuantityColumnDefinition(
  structuredTableColumn: QuantityStructuredTableColumn,
  canChangeUnits: boolean
): ColumnDefinition {
  return {
    type: TableColumnType.QUANTITY,
    units: structuredTableColumn.units,
    hideContextMenu: !canChangeUnits,
    columnGroup: structuredTableColumn.columnGroup,
    locked: isReadOnly(structuredTableColumn),
    isRequired: structuredTableColumn.isRequired,
  };
}

function toConcentrationColumnDefinition(
  structuredTableColumn: ConcentrationStructuredTableColumn,
  canChangeUnits: boolean
): ColumnDefinition {
  return {
    type: TableColumnType.CONCENTRATION,
    units: structuredTableColumn.units,
    hideContextMenu: !canChangeUnits,
    columnGroup: structuredTableColumn.columnGroup,
    locked: isReadOnly(structuredTableColumn),
    isRequired: structuredTableColumn.isRequired,
  };
}

function toTagValueColumnDefinition(
  schemas: Array<Schema | BatchSchema>,
  structuredTableColumn: TagValueStructuredTableColumn
): SchemaFieldColumnDefinition | ArchivedFieldColumnDefinition | MissingFieldColumnDefinition {
  if (isMissingFieldColumn(structuredTableColumn, schemas)) {
    // The field was deleted or we don't have permissions to it
    return {
      type: TableColumnType.MISSING_FIELD,
      structuredTableColumnName: structuredTableColumn.name,
      isArchived: structuredTableColumn.isArchived,
      columnGroup: structuredTableColumn.columnGroup,
      locked: isReadOnly(structuredTableColumn),
      isRequired: structuredTableColumn.isRequired,
    };
  }

  const schemaField = findMatchingSchemaFieldForColumn(schemas, structuredTableColumn);
  assert(
    schemaField != null,
    'Matching schema field should exist if the column is not a missing field column'
  );

  return toSchemaFieldColumnDefinition(
    schemaField,
    structuredTableColumn as BaseTagValueStructuredTableColumn<ClientId>
  );
}

function toColumnDefinitionByColumnType({
  structuredTableColumn,
  stageRuns,
  schemas,
  searchAllSchemas,
}: {
  structuredTableColumn: StructuredTableColumn;
  stageRuns?: Array<StageRun>;
  schemas: Array<Schema | BatchSchema | unknown>;
  searchAllSchemas?: boolean;
}): ColumnDefinition {
  const tagSchema: Schema | undefined = schemas.find(SchemaPredicates.isTagSchema);
  const schemaInterface: SchemaInterfaceSummary | undefined = schemas.find(
    SchemaPredicates.isSchemaInterface
  );
  const {columnGroup, isRequired, name} = structuredTableColumn;
  const locked = isReadOnly(structuredTableColumn);
  const baseColumnDefinition = {
    columnGroup,
    locked,
    isRequired,
    name: name !== '' ? name : undefined,
  };
  switch (structuredTableColumn.columnType) {
    case TableColumnType.ALIAS:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.ALIAS,
      };
    case TableColumnType.BATCH:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.BATCH,
      };
    case TableColumnType.BIOENTITY_NAME:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.BIOENTITY_NAME,
      };
    case TableColumnType.DNA_BASES:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.DNA_BASES,
      };
    case TableColumnType.RNA_BASES:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.RNA_BASES,
      };
    case TableColumnType.DNA_HELM:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.DNA_HELM,
      };
    case TableColumnType.RNA_HELM:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.RNA_HELM,
      };
    case TableColumnType.DNA_IDT:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.DNA_IDT,
      };
    case TableColumnType.RNA_IDT:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.RNA_IDT,
      };
    case TableColumnType.BASES:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.BASES,
        basesColumnConfig: (structuredTableColumn as BasesStructuredTableColumn).basesColumnConfig,
      };
    case TableColumnType.SEQUENCE_TOPOLOGY:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.SEQUENCE_TOPOLOGY,
      };
    case TableColumnType.SMILES:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.SMILES,
      };
    case TableColumnType.EXISTING_BIOENTITY: {
      const existingBioentityColumn =
        structuredTableColumn as BaseExistingBioentityStructuredTableColumn<ClientId>;
      if (tagSchema == null && schemaInterface == null && !searchAllSchemas) {
        // Possibly because the user doesn't have permissions on the schema
        return {
          ...baseColumnDefinition,
          name: name != null ? name : EXISTING_BIOENTITY_COLUMN_NAME,
          type: TableColumnType.STRING,
          locked: true,
        };
      }
      return {
        ...baseColumnDefinition,
        type: TableColumnType.EXISTING_BIOENTITY,
        tagSchemaOrInterface: searchAllSchemas ? 'ALL' : (tagSchema ?? schemaInterface ?? 'ALL'),
        bioentityTypeFilters: existingBioentityColumn.bioentityTypeFilters,
      };
    }
    case TableColumnType.BIOENTITY_SELECTOR:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.BIOENTITY_SELECTOR,
        schemaFieldForFiltering: (structuredTableColumn as BioentitySelectorStructuredTableColumn)
          .schemaFieldForFiltering,
      };
    case TableColumnType.PARTIES:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.PARTIES,
      };
    case TableColumnType.SAMPLE_RESTRICTION_STATUS:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.SAMPLE_RESTRICTION_STATUS,
      };
    case TableColumnType.STAGE_RUN:
      assert(stageRuns != null, 'A STAGE_RUN column type must have a stage runs field');
      return {
        ...baseColumnDefinition,
        type: TableColumnType.STAGE_RUN,
        stageRuns,
      };
    case TableColumnType.SAMPLE:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.SAMPLE,
      };
    case TableColumnType.PROCESS_TASK:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.PROCESS_TASK,
        schemaId: findProcessRelatedSchemaId(schemas.map(({id}) => id)),
      };
    case TableColumnType.PROCESS_TASK_ASSIGNEE:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.PROCESS_TASK_ASSIGNEE,
      };
    case TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN,
      };
    case TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE,
      };
    case TableColumnType.PROCESS_OUTPUT:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.PROCESS_OUTPUT,
      };
    case TableColumnType.PROCEDURE:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.PROCEDURE,
        procedureTypes: (structuredTableColumn as ProcedureStructuredTableColumn).procedureTypes,
        placeholder: (structuredTableColumn as ProcedureStructuredTableColumn).placeholder,
        shouldAllowMultiple: (structuredTableColumn as ProcedureStructuredTableColumn).shouldAllowMultiple,
      };
    case TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE,
        context: (structuredTableColumn as ProcedureSchemaFieldTypeStructuredTableColumn).context,
      };
    case TableColumnType.PROCEDURE_CONDITION_REPLICATES:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.PROCEDURE_CONDITION_REPLICATES,
      };
    case TableColumnType.TEST_DEFINITION:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.TEST_DEFINITION,
        targetOrganization: (structuredTableColumn as TestDefinitionStructuredTableColumn).targetOrganization,
      };
    case TableColumnType.NUMBER_OF_CONTAINERS:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.NUMBER_OF_CONTAINERS,
      };
    case TableColumnType.POSITION:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.POSITION,
        // TODO(BNCH-6310): backfill old tables' destination columns to have {columnGroup: 'TARGET'}
        columnGroup: columnGroup || TransferTableColumnGroup.TARGET,
      };
    case TableColumnType.OPTION_SELECTOR:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.OPTION_SELECTOR,
        options: (structuredTableColumn as OptionSelectorStructuredTableColumn).options,
      };
    case TableColumnType.OPTION_SEARCH_SELECTOR:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.OPTION_SEARCH_SELECTOR,
        options: (structuredTableColumn as OptionSearchSelectorStructuredTableColumn).options,
        isMulti: (structuredTableColumn as OptionSearchSelectorStructuredTableColumn).isMulti,
      };
    case TableColumnType.STRING:
      return {
        ...baseColumnDefinition,
        name,
        type: TableColumnType.STRING,
      };
    case TableColumnType.BOOLEAN:
      return {
        ...baseColumnDefinition,
        name,
        type: TableColumnType.BOOLEAN,
      };
    case TableColumnType.INGREDIENT_CATALOG_ID:
    case TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT:
    case TableColumnType.INGREDIENT_NOTES:
      return {
        ...baseColumnDefinition,
        type: structuredTableColumn.columnType,
      };
    case TableColumnType.BULK_ASSEMBLY_BIN_SELECTOR:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.BULK_ASSEMBLY_BIN_SELECTOR,
        bins: (structuredTableColumn as BulkAssemblyBinSelectorStructuredTableColumn).bins,
      };
    case TableColumnType.BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR,
        fragmentDatas: (structuredTableColumn as BulkAssemblyFragmentSelectorStructuredTableColumn)
          .fragmentDatas,
        binColor: (structuredTableColumn as BulkAssemblyFragmentSelectorStructuredTableColumn).binColor,
        method: (structuredTableColumn as BulkAssemblyFragmentSelectorStructuredTableColumn).method,
        assemblyId: (structuredTableColumn as BulkAssemblyFragmentSelectorStructuredTableColumn).assemblyId,
      };
    case TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT,
        side: (structuredTableColumn as BulkAssemblyFragmentEndpointSelectorStructuredTableColumn).side,
      };
    case TableColumnType.BULK_ASSEMBLY_RESTRICTION_ENZYME:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.BULK_ASSEMBLY_RESTRICTION_ENZYME,
      };
    case TableColumnType.BULK_ASSEMBLY_CONSTRUCT_NAME:
    case TableColumnType.BULK_ASSEMBLY_VALIDATION_STATUS:
    case TableColumnType.BULK_ASSEMBLY_PRIMER:
    case TableColumnType.BULK_ASSEMBLY_PRIMER_BASES:
    case TableColumnType.BULK_ASSEMBLY_PREFERRED_PRIMER:
    case TableColumnType.BULK_ASSEMBLY_LENGTH:
      return {
        ...baseColumnDefinition,
        type: structuredTableColumn.columnType,
      };
    case TableColumnType.BULK_ASSEMBLY_FRAGMENT_POLYMER:
      return {
        ...baseColumnDefinition,
        type: structuredTableColumn.columnType,
        bioentityTypeFilters: (structuredTableColumn as BulkAssemblyFragmentSequenceStructuredTableColumn)
          .bioentityTypeFilters,
      };
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CHIP: {
      return {
        ...baseColumnDefinition,
        type: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CHIP,
        frozenEntityChipData: (structuredTableColumn as FillPlateSourceChipStructuredTableColumn)
          .frozenEntityChipData,
        reagentGroupsById: (structuredTableColumn as FillPlateSourceChipStructuredTableColumn)
          .reagentGroupsById,
        testOrderInfoById: (structuredTableColumn as FillPlateSourceChipStructuredTableColumn)
          .testOrderInfoById,
      };
    }
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP: {
      return {
        ...baseColumnDefinition,
        type: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP,
        frozenEntityChipData: (structuredTableColumn as FillPlateSourceContainerChipStructuredTableColumn)
          .frozenEntityChipData,
        reagentGroupsById: (structuredTableColumn as FillPlateSourceContainerChipStructuredTableColumn)
          .reagentGroupsById,
        testOrderInfoById: (structuredTableColumn as FillPlateSourceContainerChipStructuredTableColumn)
          .testOrderInfoById,
      };
    }
    case TableColumnType.PLATE_MAP_MATERIAL_INPUT: {
      return {
        ...baseColumnDefinition,
        type: TableColumnType.PLATE_MAP_MATERIAL_INPUT,
        plateMaterialInputs: (structuredTableColumn as PlateMapMaterialInputStructuredTableColumn)
          .plateMaterialInputs,
      };
    }
    case TableColumnType.ANONYMOUS_TAG_VALUE:
    case TableColumnType.EXPERIMENTAL_ROLE:
    case TableColumnType.FILL_PLATE_MEASUREMENT_AMOUNT:
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_SCHEMA_CHIP:
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_ICON:
      return {
        ...baseColumnDefinition,
        type: structuredTableColumn.columnType,
      };
    case TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN,
        navigateToStep: (structuredTableColumn as ProcedureParameterConfirmedInStructuredTableColumn)
          .navigateToStep,
      };
    case TableColumnType.STUDIES:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.STUDIES,
      };
    case TableColumnType.DEDICATED_LOOKUP: {
      const lookupColumn = structuredTableColumn as DedicatedLookupStructuredTableColumn;
      return {
        ...baseColumnDefinition,
        type: lookupColumn.columnType,
        name: lookupColumn.name,
        inputColumnProvenanceUuid: lookupColumn.inputColumnProvenanceUuid,
        lookupConfig: lookupColumn.lookupConfig,
        lookupSortType: lookupColumn.lookupSortType,
      };
    }
    case TableColumnType.CUSTOM:
      return {
        ...baseColumnDefinition,
        type: structuredTableColumn.columnType,
        name: structuredTableColumn.name,
        customColumnConfig: (structuredTableColumn as CustomStructuredTableColumn).customColumnConfig,
      };
    case TableColumnType.ROW_DELEGATE:
      return {
        ...baseColumnDefinition,
        type: structuredTableColumn.columnType,
        name: structuredTableColumn.name,
      };
    case TableColumnType.COMPLEX_POLYMER_COMPONENT: {
      assert(tagSchema != null, 'Tag schema must be defined for COMPLEX_POLYMER_COMPONENT column type');
      const column = structuredTableColumn as ComplexPolymerComponentStructuredTableColumn;

      const entitySelectionMode =
        column.frontEndMetadata?.allowsMultipleComponents === true
          ? EntitySelectionMode.MULTIPLE_WITH_DUPLICATES
          : EntitySelectionMode.SINGLE;

      return {
        ...baseColumnDefinition,
        type: TableColumnType.COMPLEX_POLYMER_COMPONENT,
        componentSchemaField: findComplexPolymerComponentSchemaField(column, tagSchema),
        entitySelectionMode,
      };
    }
    case TableColumnType.COMPLEX_POLYMER_CONNECTIONS:
      return {
        ...baseColumnDefinition,
        type: TableColumnType.COMPLEX_POLYMER_CONNECTIONS,
      };
    case TableColumnType.OLIGO_DUPLEX_OFFSET:
      return {
        ...baseColumnDefinition,
        type: structuredTableColumn.columnType,
      };
    case TableColumnType.ANTIBODY_DOMAIN: {
      const column = structuredTableColumn as AntibodyDomainStructuredTableColumn;
      return {
        ...baseColumnDefinition,
        type: TableColumnType.ANTIBODY_DOMAIN,
        domainType: column.columnMetadata.domainType,
        specificity: column.columnMetadata.specificity,
      };
    }
    default:
      throw new Error(`Unexpected column type received: ${structuredTableColumn.columnType}`);
  }
}

function findComplexPolymerComponentSchemaField(
  structuredTableColumn: ComplexPolymerComponentStructuredTableColumn,
  schema?: Schema | null
): BioentityLinkSchemaField | null {
  if (schema == null) {
    return null;
  }
  const schemaFieldId = structuredTableColumn.schemaFieldApiId;
  const schemaField = schema.allFields.find((field) => field.apiId === schemaFieldId);
  assertAndContinue(schemaField != null, 'Expected schema field to exist.');
  return (schemaField as BioentityLinkSchemaField) ?? null;
}

function toParentStorableColumnDefinition({
  structuredTableColumn,
  storableSchema,
  parentStorableIsGrid,
  parentStorableType,
  fallbackRegistryId,
  structuredTableKind,
}: {
  structuredTableColumn: StructuredTableColumn;
  storableSchema?: StorableSchema | null;
  parentStorableIsGrid?: boolean;
  parentStorableType?: ParentStorableType;
  fallbackRegistryId?: string | null;
  structuredTableKind?: StructuredTableKind;
}): ColumnDefinition {
  const {columnGroup, isRequired} = structuredTableColumn;
  const derivedParentStorableType = parentStorableIsGrid
    ? ParentStorableType.GRID
    : ParentStorableType.LOCATION;
  return {
    columnGroup,
    locked: isReadOnly(structuredTableColumn),
    type: TableColumnType.PARENT_STORABLE,
    isForTransfer: structuredTableKind
      ? ![StructuredTableKind.INVENTORY, StructuredTableKind.RUN_OUTPUT_SAMPLES].includes(structuredTableKind)
      : true,
    storableSchema,
    parentStorableIsGrid,
    parentStorableType: parentStorableType ?? derivedParentStorableType,
    fallbackRegistryId,
    isRequired,
    ...(structuredTableColumn.name !== '' ? {name: structuredTableColumn.name} : {}),
  };
}

function toUnitSelectorColumnDefinition(
  structuredTableColumn: UnitSelectorStructuredTableColumn
): UnitSelectorColumnDefinition {
  const {columnGroup, isRequired, frontEndMetadata, name} = structuredTableColumn;
  const acceptedUnits = frontEndMetadata?.acceptedUnits ?? [];
  assert(
    acceptedUnits.length > 0,
    'The unit selector column needs to have its `frontEndMetadata.acceptedUnits` values set'
  );
  return {
    type: TableColumnType.UNIT_SELECTOR,
    columnGroup,
    locked: isReadOnly(structuredTableColumn),
    isRequired,
    acceptedUnits,
    name,
  };
}

function toUnitReferenceColumnDefinition(
  structuredTableColumn: UnitReferenceStructuredTableColumn
): UnitReferenceColumnDefinition {
  const {columnGroup, isRequired, frontEndMetadata, name} = structuredTableColumn;

  return {
    columnGroup,
    isRequired,
    locked: isReadOnly(structuredTableColumn),
    name: name !== '' ? name : undefined,
    type: TableColumnType.UNIT_REFERENCE,
    unitTypesFilterPredicateType:
      frontEndMetadata?.unitTypesFilterPredicate &&
      getPredicateType(frontEndMetadata?.unitTypesFilterPredicate),
    displayMode: frontEndMetadata?.displayMode ?? UnitReferenceColumnDisplayMode.CHIP,
    ignoreErrorWhenTypedValue: frontEndMetadata?.ignoreErrorWhenTypedValue,
  };
}

function toMeasurementAmountColumnDefinition(
  structuredTableColumn: MeasurementAmountStructuredTableColumn,
  structuredTableKind?: StructuredTableKind
): MeasurementAmountColumnDefinition {
  const {columnGroup, isRequired, frontEndMetadata, name} = structuredTableColumn;
  const isComputed = frontEndMetadata?.isComputed ?? false;
  const allowQS = Boolean(frontEndMetadata?.allowQS);
  const allowNegative =
    structuredTableColumn.columnGroup === TransferTableColumnGroup.TARGET &&
    structuredTableKind === StructuredTableKind.INVENTORY;
  const allowZero = frontEndMetadata?.allowZero ?? false;

  return {
    type: TableColumnType.MEASUREMENT_AMOUNT,
    columnGroup,
    locked: isReadOnly(structuredTableColumn),
    isRequired,
    isComputed,
    name,
    allowQS,
    allowNegative,
    allowZero,
  };
}

function getModeOfContainersColumn(
  structuredTableColumn: ContainersStructuredTableColumn,
  inventoryStructuredTableMode?: InventoryStructuredTableMode
): ContainersColumnMode {
  if (structuredTableColumn.columnGroup === StructuredTableColumnGroups.INGREDIENT_COMPONENT_LOT_STORAGE) {
    return ContainersColumnMode.INGREDIENT_COMPONENT_LOT;
  }
  if (!inventoryStructuredTableMode) {
    return ContainersColumnMode.LEGACY;
  }
  if (inventoryStructuredTableMode === InventoryStructuredTableMode.UPDATE_CONTAINERS) {
    return ContainersColumnMode.INVENTORY_CONTAINER;
  }
  if (structuredTableColumn.columnGroup === TransferTableColumnGroup.SOURCE) {
    return ContainersColumnMode.SOURCE_FOR_TRANSFER_TABLE;
  }
  assert(
    structuredTableColumn.columnGroup === TransferTableColumnGroup.TARGET,
    'Containers column must have either SOURCE or TARGET as column group'
  );
  return inventoryStructuredTableMode === InventoryStructuredTableMode.CREATE
    ? ContainersColumnMode.NEW_DESTINATION_FOR_TRANSFER_TABLE
    : ContainersColumnMode.EXISTING_DESTINATION_FOR_TRANSFER_TABLE;
}

function toContainersColumnDefinition(
  structuredTableColumn: ContainersStructuredTableColumn,
  inventoryStructuredTableMode?: InventoryStructuredTableMode
): ColumnDefinition {
  const {name, columnGroup, isRequired, frontEndMetadata} = structuredTableColumn;
  return {
    type: TableColumnType.CONTAINERS,
    // TODO(BNCH-6310): backfill old tables' destination columns to have {columnGroup: 'TARGET'}
    columnGroup: columnGroup || TransferTableColumnGroup.TARGET,
    locked: isReadOnly(structuredTableColumn),
    isRequired,
    name: name !== '' ? name : undefined,
    containersColumnMode: getModeOfContainersColumn(structuredTableColumn, inventoryStructuredTableMode),
    containerSchemaId: frontEndMetadata?.containerSchemaId,
    containerSchemaName: frontEndMetadata?.containerSchemaName,
  };
}

function toBoxColumnDefinition(
  structuredTableColumn: BoxStructuredTableColumn,
  schemas: Array<Schema | BatchSchema>
): ColumnDefinition {
  const {columnGroup, isRequired, name} = structuredTableColumn;
  const locked = isReadOnly(structuredTableColumn);
  // prefer finding the box schema, but fallback to any storable schema
  const storableSchema =
    schemas.find((schema) => APIUtil.isBoxSchema(schema.id)) ??
    schemas.find((schema) => APIUtil.isStorableSchema(schema.id));
  const containerSchema = schemas.find((schema) => APIUtil.isContainerSchema(schema.id));
  return {
    type: TableColumnType.BOX,
    columnGroup,
    locked,
    isRequired,
    name: name !== '' ? name : undefined,
    registryId: storableSchema != null ? (storableSchema as StorableSchema).registryId : null,
    boxSchemaId: storableSchema?.id,
    boxSchemaName: storableSchema?.name,
    ...(containerSchema != null && {containerSchema: {id: containerSchema.id, name: containerSchema.name}}),
  };
}

function toPlateColumnDefinition(
  structuredTableColumn: PlateStructuredTableColumn,
  schemas: Array<Schema | BatchSchema>,
  fallbackRegistryId?: string | null,
  destinationType?: TransferStructuredTableDestinationType
): ColumnDefinition {
  const {columnGroup, isRequired, name} = structuredTableColumn;
  const locked = isReadOnly(structuredTableColumn);
  const plateSchema = schemas.find((schema): schema is PlateSchema => APIUtil.isPlateSchema(schema.id));
  const anyStorableSchema =
    plateSchema ?? schemas.find((schema): schema is StorableSchema => APIUtil.isStorableSchema(schema.id));
  const containerSchema = schemas.find((schema) => APIUtil.isContainerSchema(schema.id));

  assert(
    fallbackRegistryId == null ||
      anyStorableSchema == null ||
      anyStorableSchema.registryId === fallbackRegistryId,
    'The schema & fallback registryIds must match if both provided for PLATE column definition'
  );
  return {
    type: TableColumnType.PLATE,
    columnGroup,
    locked,
    isRequired,
    name: name !== '' ? name : undefined,
    registryId: anyStorableSchema != null ? anyStorableSchema.registryId : (fallbackRegistryId ?? null),
    plateSchemaId: plateSchema?.id,
    plateSchemaName: plateSchema?.name,
    plateSchemaType:
      destinationType === TransferStructuredTableDestinationType.MATRIX_PLATE
        ? PlateSchemaType.MATRIX_PLATE
        : undefined,
    ...(containerSchema != null && {containerSchema: {id: containerSchema.id, name: containerSchema.name}}),
  };
}

function toProcessTaskStatusSelectorColumnDefinition(
  structuredTableColumn: ProcessTaskStatusStructuredTableColumn
): ProcessTaskStatusColumnDefinition {
  const {columnGroup, isRequired, name} = structuredTableColumn;
  return {
    type: TableColumnType.PROCESS_TASK_STATUS,
    columnGroup,
    locked: isReadOnly(structuredTableColumn),
    isRequired,
    statusLifecycle: structuredTableColumn.frontEndMetadata?.statusLifecycle,
    name,
  };
}

/**
 * Return the columns definition constructed from structuredTableColumn.
 *
 * null is returned in case of failure, for example if the column definition required a schema, but the
 * schema is missing in schemas parameter.
 */
export default function toColumnDefinition({
  structuredTableColumn,
  stageRuns,
  canChangeUnits,
  schemas,
  parentStorableIsGrid,
  parentStorableType,
  searchAllSchemas,
  inventoryStructuredTableMode,
  structuredTableKind,
  fallbackRegistryId,
  destinationType,
}: {
  structuredTableColumn: StructuredTableColumn;
  stageRuns?: Array<StageRun>;
  canChangeUnits?: boolean;
  schemas: Array<Schema | BatchSchema>;
  parentStorableIsGrid?: boolean;
  parentStorableType?: ParentStorableType;
  searchAllSchemas?: boolean;
  inventoryStructuredTableMode?: InventoryStructuredTableMode;
  structuredTableKind?: StructuredTableKind;
  fallbackRegistryId?: string | null;
  destinationType?: TransferStructuredTableDestinationType;
}): ColumnDefinition {
  if (isQuantityStructuredTableColumn(structuredTableColumn)) {
    assert(canChangeUnits != null, 'A VOLUME column type must specify if we can change units');
    return toQuantityColumnDefinition(structuredTableColumn, canChangeUnits);
  } else if (isConcentrationStructuredTableColumn(structuredTableColumn)) {
    assert(canChangeUnits != null, 'A CONCENTRATION column type must specify if we can change units');
    return toConcentrationColumnDefinition(structuredTableColumn, canChangeUnits);
  } else if (isParentStorableStructuredTableColumn(structuredTableColumn)) {
    const storableSchema = schemas.find(SchemaPredicates.isStorableSchema);
    if (!isReadOnly(structuredTableColumn)) {
      assert(
        parentStorableIsGrid != null || parentStorableType != null,
        'A PARENT_STORABLE column type must have a parentStorableIsGrid or parentStorableType attribute when editable'
      );
    }
    return toParentStorableColumnDefinition({
      structuredTableColumn,
      storableSchema,
      parentStorableIsGrid,
      parentStorableType,
      fallbackRegistryId,
      structuredTableKind,
    });
  } else if (isExistingBioentityStructuredTableColumn(structuredTableColumn)) {
    const validSchemas = structuredTableColumn.frontEndMetadata?.validSchemas;
    return toColumnDefinitionByColumnType({
      structuredTableColumn,
      stageRuns,
      schemas: Array.isArray(validSchemas) ? validSchemas : schemas,
      searchAllSchemas: searchAllSchemas ?? validSchemas === 'ALL',
    });
  } else if (isTagValueStructuredTableColumn(structuredTableColumn)) {
    return toTagValueColumnDefinition(schemas, structuredTableColumn);
  } else if (isUnitSelectorStructuredTableColumn(structuredTableColumn)) {
    return toUnitSelectorColumnDefinition(structuredTableColumn);
  } else if (isMeasurementAmountStructuredTableColumn(structuredTableColumn)) {
    return toMeasurementAmountColumnDefinition(structuredTableColumn, structuredTableKind);
  } else if (isContainersStructuredTableColumn(structuredTableColumn)) {
    return toContainersColumnDefinition(structuredTableColumn, inventoryStructuredTableMode);
  } else if (isBoxStructuredTableColumn(structuredTableColumn)) {
    return toBoxColumnDefinition(structuredTableColumn, schemas);
  } else if (isPlateStructuredTableColumn(structuredTableColumn)) {
    return toPlateColumnDefinition(structuredTableColumn, schemas, fallbackRegistryId, destinationType);
  } else if (isProcessTaskStatusStructuredTableColumn(structuredTableColumn)) {
    return toProcessTaskStatusSelectorColumnDefinition(structuredTableColumn);
  } else if (isUnitReferenceStructuredTableColumn(structuredTableColumn)) {
    return toUnitReferenceColumnDefinition(structuredTableColumn);
  } else {
    return toColumnDefinitionByColumnType({structuredTableColumn, stageRuns, schemas, searchAllSchemas});
  }
}
